// RECIPETABLE1 (docs/build-briefs/RECIPETABLE1-recipes-table.md). The pure part of the Recipes table's bulk strip: the sentences the
// person reads and the running total of the server's answers. No DOM, no calls. The batch size is the inventory list's own.

import type { BulkMenuDrinkFailureDto, BulkMenuDrinkResultDto } from "./api.ts";
import { exactDollars } from "./franchisorListModel.ts";

export { toBatches, BULK_EDIT_BATCH_SIZE as BULK_RECIPE_BATCH_SIZE } from "./bulkEditModel.ts";

export type BulkRecipeField = "category" | "active" | "requiresLiquor" | "buildCharge";

export const BULK_RECIPE_COPY = {
  category: "Display category",
  active: "Active / Inactive",
  requiresLiquor: "Requires liquor",
  buildCharge: "Build charge",
  selectAllShown: "Select all",
  clear: "Clear",
  cancel: "Cancel",
  done: "Done",
  noCategories: "No display categories yet. Add one in Settings, Menu first.",
  noLiquorCategories: "No liquor categories yet. Add one in Settings, Menu first.",
  pickOne: "Pick one to continue.",
  pickLiquorCategory: "Pick at least one liquor category.",
  enterCharge: "Enter the build charge to continue.",
  liquorCategoriesLabel: "Allowed liquor categories",
  buildChargeLabel: "Build charge",
  applying: "Saving",
  onWord: "On",
  offWord: "Off",
  activeWord: "Active",
  inactiveWord: "Inactive",
} as const;

export function drinksWord(count: number): string {
  return count === 1 ? "1 drink" : `${count} drinks`;
}

/** "3 selected". */
export function selectedLabel(count: number): string {
  return `${count} selected`;
}

/** "Select all 41 shown". */
export function selectAllLabel(shownCount: number): string {
  return `${BULK_RECIPE_COPY.selectAllShown} ${shownCount} shown`;
}

/** The sheet title: "Display category for 12 drinks". */
export function sheetTitle(field: BulkRecipeField, count: number): string {
  return `${BULK_RECIPE_COPY[field]} for ${drinksWord(count)}`;
}

export interface BulkRecipeChoice {
  field: BulkRecipeField;
  /** The picked display category label. */
  categoryLabel?: string;
  /** True to make active. */
  active?: boolean;
  /** True to turn Requires liquor on. */
  requires?: boolean;
  /** The picked liquor category labels, in the order shown. */
  liquorLabels?: readonly string[];
  /** The build charge in cents. */
  chargeCents?: number;
}

/** The sentence the person confirms, naming the change and the count. */
export function confirmSentence(choice: BulkRecipeChoice, count: number): string {
  const drinks = drinksWord(count);
  switch (choice.field) {
    case "category":
      return `Set ${choice.categoryLabel ?? ""} as the display category for ${drinks}.`;
    case "active":
      return choice.active === true
        ? `Make ${drinks} active.`
        : `Make ${drinks} inactive. Guests won't see ${count === 1 ? "it" : "them"} on the menu until made active again. Nothing already on a ticket is affected.`;
    case "requiresLiquor":
      return choice.requires === true
        ? `Turn on Requires liquor for ${drinks}. Allowed: ${(choice.liquorLabels ?? []).join(", ")}.`
        : `Turn off Requires liquor for ${drinks}.`;
    case "buildCharge":
      return `Set the build charge to ${exactDollars(choice.chargeCents ?? 0)} for ${drinks}.`;
  }
}

/** The primary button once a choice is made: "Set Gin for 12 drinks". */
export function applyButtonLabel(choice: BulkRecipeChoice, count: number): string {
  const drinks = drinksWord(count);
  switch (choice.field) {
    case "category":
      return `Set ${choice.categoryLabel ?? ""} for ${drinks}`;
    case "active":
      return choice.active === true ? `Make ${drinks} active` : `Make ${drinks} inactive`;
    case "requiresLiquor":
      return choice.requires === true ? `Turn on for ${drinks}` : `Turn off for ${drinks}`;
    case "buildCharge":
      return `Set ${exactDollars(choice.chargeCents ?? 0)} for ${drinks}`;
  }
}

export interface BulkRecipeTotals {
  changed: number;
  alreadySet: number;
  failed: BulkMenuDrinkFailureDto[];
}

export function emptyTotals(): BulkRecipeTotals {
  return { changed: 0, alreadySet: 0, failed: [] };
}

/** Adds one batch's answer to the running total. */
export function addBatch(totals: BulkRecipeTotals, batch: BulkMenuDrinkResultDto): BulkRecipeTotals {
  return {
    changed: totals.changed + batch.changed,
    alreadySet: totals.alreadySet + batch.alreadySet,
    failed: [...totals.failed, ...batch.failed],
  };
}

/** "12 changed, 2 already set, 3 could not be changed." Every part is always said, so a zero is visible. */
export function resultSentence(totals: BulkRecipeTotals): string {
  return `${totals.changed} changed, ${totals.alreadySet} already set, ${totals.failed.length} could not be changed.`;
}

/** The line when the run stopped early: what was done, and that running it again skips finished drinks. */
export function stoppedSentence(doneCount: number, totalCount: number, message: string): string {
  return `Stopped after ${doneCount} of ${totalCount} drinks. ${message} Press the action again to continue; drinks already set are skipped.`;
}
